import type { SelfContainedCommand, CommandExecutionContext } from 'types/commands';
import type { StandardToolResult } from 'types/standardToolResults';
import { createSuccessResult, createErrorResult } from 'types/standardToolResults';
import { createFileLogger } from '../../../../utils/logger';
import { entityCreationRegistry } from '../../../../services/entityCreationRegistry';

const log = createFileLogger('PROMPT_CREATE');

export const promptCreateCommand: SelfContainedCommand = {
  id: 'prompt_create',
  description: '⚡ CREATE: Create a new reusable AI prompt.',
  autonomyLevel: 'auto',
  visualTreatment: 'demonstrative',

  schema: {
    parameters: [
      {
        name: 'name',
        type: 'string',
        description: 'Display name for the prompt',
        required: false,
        defaultValue: 'New Prompt',
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space to create the prompt in',
        required: false,
      },
      {
        name: 'folderId',
        type: 'string',
        description: 'Folder to place the prompt in',
        required: false,
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to open the prompt editor after creation. Default true.',
        required: false,
        defaultValue: true,
      },
    ],
  },

  examples: {
    good: [
      {
        input: { name: 'Summarize Article' },
        expectedOutput: createSuccessResult('Prompt created', { entityId: 'prompt-123' }),
        description: 'Create a new prompt',
      },
    ],
    bad: [],
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const {
      name = 'New Prompt',
      spaceId: providedSpaceId,
      folderId,
      createWindow = true,
    } = params;

    const spaceId = providedSpaceId || context.spaceId;

    try {
      log.log('[prompt_create] Creating prompt:', { name, spaceId });

      const entityStoreModule = await import('../../../../stores/entityStore');
      const entityStore = entityStoreModule.default.getState();

      const entity = await entityStore.actions.createEntity({
        entityType: 'Prompt',
        name: name.trim(),
        spaceId,
        skeleton: {
          content: '',
          runCount: 0,
          model: null,
          groupIds: folderId ? [folderId] : [],
        },
      });

      const entityId = entity._id || entity.id;

      if (entityId && spaceId) {
        try {
          const parentId = folderId || spaceId;
          const { default: useDataspaceTreeStore } = await import('../../../../stores/dataspaceTreeStore');
          const treeStore = useDataspaceTreeStore.getState();
          treeStore._applyAddNode(`${parentId}:${entityId}`, {
            id: entityId,
            parentId,
            pos: 'M',
            expanded: false,
            kind: 'canonical',
          });
        } catch (treeErr) {
          log.warn('[prompt_create] Tree registration failed (non-fatal):', treeErr);
        }
      }

      if (createWindow !== false && entityId) {
        try {
          const { useCommandRegistryStore } = await import('../../../../stores/commandRegistryStore');
          const { executeCommand } = useCommandRegistryStore.getState();
          await executeCommand('space_open_window', { entityId });
        } catch (windowErr) {
          log.warn('[prompt_create] Failed to open window (non-fatal):', windowErr);
        }
      }

      log.log('[prompt_create] Prompt created:', entityId);

      return createSuccessResult(`Prompt created: "${name.trim()}"`, {
        entityId,
        name: name.trim(),
      });
    } catch (err: any) {
      log.error('[prompt_create] Failed:', err);
      return createErrorResult('Failed to create prompt', err.message || 'Unknown error', 'execution');
    }
  },
};

entityCreationRegistry.register({
  commandId: 'prompt_create',
  entityType: 'Prompt',
  displayName: 'Prompt',
  icon: {
    type: 'phosphor',
    phosphorIcon: 'Lightning',
    phosphorWeight: 'duotone',
  },
  description: 'Create a reusable AI prompt',
  canCreateInFolder: true,
  openAfterCreate: true,
  category: 'system',
  sortOrder: 20,
});
